import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createAdminUser,
  fetchAccessPreview,
  fetchAdminHealth,
  fetchAdminUsers,
  fetchAuditLog,
  fetchOrgTree,
  fetchRolesCatalog,
  fetchRoutesConfig,
  orgMutations,
  refreshAdminHealth,
  routesMutations,
  setUserActive,
  setUserLogin,
  setUserRoles,
  setUserTeam,
  type AuditFilters,
  type CreateUserPayload,
  type RoleConfig,
} from "@/api/adminCenter";

const USERS_KEY = ["adminCenter", "users"] as const;

export function useAdminUsers(enabled = true) {
  return useQuery({
    queryKey: USERS_KEY,
    queryFn: ({ signal }) => fetchAdminUsers(signal),
    staleTime: 15_000,
    // Не стріляти для non-admin — без spurious 403 у консолі.
    enabled,
  });
}

/** Preview compute = on-enter крок ⑥ (MP10) — окремий query по конфігу. */
export function useAccessPreview(cfg: RoleConfig | null) {
  return useQuery({
    queryKey: ["adminCenter", "accessPreview", cfg],
    queryFn: ({ signal }) => fetchAccessPreview(cfg as RoleConfig, signal),
    enabled: cfg !== null,
    staleTime: 0, // recalc-on-back (MP10)
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateUserPayload) => createAdminUser(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: USERS_KEY }),
  });
}

export function useSetUserRoles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, cfg }: { userId: string; cfg: RoleConfig }) => setUserRoles(userId, cfg),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
      // S2: каталог «N носіїв» залежить від ролей (MP9 — resolver/UI негайно)
      qc.invalidateQueries({ queryKey: ["adminCenter", "rolesCatalog"] });
    },
  });
}

export function useSetUserTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, teamId }: { userId: string; teamId: string | null }) =>
      setUserTeam(userId, teamId),
    onSuccess: () => qc.invalidateQueries({ queryKey: USERS_KEY }),
  });
}

/** D-123 login-edit — post-write refresh: список (loginMismatch/badge свіжі) +
 *  аудит (новий user_login_change рядок). */
export function useSetUserLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, userName }: { userId: string; userName: string }) =>
      setUserLogin(userId, userName),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
      qc.invalidateQueries({ queryKey: ["adminCenter", "audit"] });
    },
  });
}

// ── D-044 S2 — #3 Org-tree + #4 Roles matrix ────────────────────────────────

const ORG_KEY = ["adminCenter", "orgTree"] as const;
const CATALOG_KEY = ["adminCenter", "rolesCatalog"] as const;

export function useOrgTree(enabled = true) {
  return useQuery({
    queryKey: ORG_KEY,
    queryFn: ({ signal }) => fetchOrgTree(signal),
    staleTime: 15_000,
    enabled,
  });
}

export function useRolesCatalog(enabled = true) {
  return useQuery({
    queryKey: CATALOG_KEY,
    queryFn: ({ signal }) => fetchRolesCatalog(signal),
    staleTime: 15_000,
    enabled,
  });
}

/** Будь-яка org-мутація інвалідовує дерево + users (team-membership віддзеркалюється в #1). */
export function useOrgMutation<K extends keyof typeof orgMutations>(kind: K) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<(typeof orgMutations)[K]>[0]) =>
      (orgMutations[kind] as (b: unknown) => Promise<unknown>)(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ORG_KEY });
      qc.invalidateQueries({ queryKey: USERS_KEY });
      qc.invalidateQueries({ queryKey: CATALOG_KEY });
    },
  });
}

// ── D-044 S3 — #6 Departments & routes ──────────────────────────────────────

const ROUTES_KEY = ["adminCenter", "routesConfig"] as const;

export function useRoutesConfig(enabled = true) {
  return useQuery({
    queryKey: ROUTES_KEY,
    queryFn: ({ signal }) => fetchRoutesConfig(signal),
    staleTime: 15_000,
    enabled,
  });
}

/** Routes-мутації інвалідовують config + #9 createable-каталог (requestTypes). */
export function useRoutesMutation<K extends keyof typeof routesMutations>(kind: K) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<(typeof routesMutations)[K]>[0]) =>
      (routesMutations[kind] as (b: unknown) => Promise<unknown>)(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROUTES_KEY });
      // D-040: createable-зміни віддзеркалюються у формі створення запиту (#9)
      qc.invalidateQueries({ queryKey: ["requestTypes"] });
      // структура (#3) ділить departments
      qc.invalidateQueries({ queryKey: ["adminCenter", "orgTree"] });
    },
  });
}

// ── D-044 S4 — #7 Health + #8 Audit ─────────────────────────────────────────

const HEALTH_KEY = ["adminCenter", "health"] as const;

export function useAdminHealth(enabled = true) {
  return useQuery({
    queryKey: HEALTH_KEY,
    queryFn: ({ signal }) => fetchAdminHealth(signal),
    staleTime: 60_000, // P3: snapshot кешований сервером (TTL 300с)
    enabled,
  });
}

/** MP1: failure → onError (старі дані queryClient НЕ перетираються) + банер «Повторити». */
export function useRefreshHealth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => refreshAdminHealth(),
    onSuccess: (data) => qc.setQueryData(HEALTH_KEY, data),
  });
}

export function useAuditLog(filters: AuditFilters, enabled = true) {
  return useQuery({
    queryKey: ["adminCenter", "audit", filters],
    queryFn: ({ signal }) => fetchAuditLog(filters, signal),
    staleTime: 15_000,
    enabled,
  });
}

export function useSetUserActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, active }: { userId: string; active: boolean }) =>
      setUserActive(userId, active),
    onSuccess: () => qc.invalidateQueries({ queryKey: USERS_KEY }),
  });
}
